import { c as _c } from "react/compiler-runtime";
import { IconButton, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import { type ResourceType, type Provider, ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { PropertyPolicyRelation, type FilterProperty } from '@/types/advanceFilter';
import { ConditionMenu } from './ConditionMenu';
import { ConditionContainer, ConditionGroup } from './ConditionViewer';
import { OperatorSelect } from './OperatorSelect';
import { PropertySelect } from './PropertySelect';
import { isGroup, type ConditionEditorOptions, type LocationIndicator } from './conditionEditorTypes';
import { inlineDropdownStyle } from './inlineDropdownStyle';
const Noop = () => null;
function ConditionGroupNode(t0) {
  const $ = _c(29);
  const {
    condition,
    parent,
    options,
    className,
    onChange,
    onChangeParent,
    setParentLocationIndicator,
    supportedProviders,
    supportedResourceTypes
  } = t0;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  let t1;
  if ($[0] !== condition) {
    t1 = isGroup(condition);
    $[0] = condition;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isGroupNode = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      display: "inline-flex"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !options.supportGrouping && isGroupNode;
  const t4 = !isGroupNode;
  let t5;
  if ($[3] !== className || $[4] !== condition.operator || $[5] !== isMenuOpen) {
    t5 = classNames(condition.operator, className, {
      hovered: isMenuOpen
    });
    $[3] = className;
    $[4] = condition.operator;
    $[5] = isMenuOpen;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== condition || $[8] !== isGroupNode || $[9] !== onChange || $[10] !== onChangeParent || $[11] !== options || $[12] !== parent || $[13] !== supportedProviders || $[14] !== supportedResourceTypes) {
    t6 = isGroupNode ? <ConditionGroupEditor supportedProviders={supportedProviders} supportedResourceTypes={supportedResourceTypes} condition={condition} options={options} onChange={onChange} onChangeParent={onChangeParent} parent={parent} /> : <AtomicConditionEditor condition={condition} onChange={onChange} options={options} supportedProviders={supportedProviders} supportedResourceTypes={supportedResourceTypes} />;
    $[7] = condition;
    $[8] = isGroupNode;
    $[9] = onChange;
    $[10] = onChangeParent;
    $[11] = options;
    $[12] = parent;
    $[13] = supportedProviders;
    $[14] = supportedResourceTypes;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== condition || $[17] !== isGroupNode || $[18] !== onChangeParent || $[19] !== options || $[20] !== parent || $[21] !== setParentLocationIndicator) {
    t7 = parent && onChangeParent && (options.supportGrouping || !isGroupNode) && <ConditionMenu condition={condition} parent={parent} options={options} onChangeParent={onChangeParent} onVisibilityChange={setIsMenuOpen} markParentLocation={setParentLocationIndicator} />;
    $[16] = condition;
    $[17] = isGroupNode;
    $[18] = onChangeParent;
    $[19] = options;
    $[20] = parent;
    $[21] = setParentLocationIndicator;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t3 || $[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7) {
    t8 = <ConditionContainer sx={t2} hideBorder={t3} enableHoverIndications={t4} className={t5}>{t6}{t7}</ConditionContainer>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}
function ConditionGroupEditor({
  condition,
  parent,
  options,
  onChange,
  onChangeParent,
  supportedProviders,
  supportedResourceTypes
}: {
  condition: Condition;
  parent?: Condition;
  options: ConditionEditorOptions;
  onChange: (condition: Condition) => void;
  onChangeParent?: (parent: Condition) => void;
  supportedProviders?: Provider[];
  supportedResourceTypes?: ResourceType[];
}) {
  const [locationIndicator, setLocationIndicator] = useState<LocationIndicator | undefined>();
  const [combineOperatorHover, setCombineOperatorHover] = useState(false);
  const onChangesFromChild = (prevCondition: Condition, newCondition: Condition) => {
    // TODO(MoLow): add tests for cascading group conditions
    const shouldCascade = parent && onChangeParent && (newCondition.conditions?.length ?? 0) <= 1;
    if (shouldCascade) {
      onChangeParent({
        ...parent,
        conditions: parent.conditions?.flatMap(c => c === prevCondition ? newCondition.conditions ?? [] : c)
      });
    } else {
      onChange(newCondition);
    }
  };
  const children = condition.conditions?.map((c_0, i) => <ConditionGroupNode supportedProviders={supportedProviders} supportedResourceTypes={supportedResourceTypes} options={options} condition={c_0} key={`${i}-${c_0.operator}-${c_0.property}`} parent={condition} className={classNames({
    hovered: combineOperatorHover,
    [`highlighted-${locationIndicator?.location}`]: locationIndicator?.index === i
  })} onChange={child => onChange({
    ...condition,
    conditions: condition.conditions?.map((cc, ii) => ii === i ? child : cc)
  })} onChangeParent={newCondition_0 => onChangesFromChild(condition, newCondition_0)} setParentLocationIndicator={setLocationIndicator} />);
  if (!parent) {
    return children;
  }
  if (!options.supportGrouping) {
    return <ConditionGroup hideBorder={true} sx={{
      padding: 0
    }}>
        {children}
      </ConditionGroup>;
  }
  return <>
      <Select onMouseEnter={() => setCombineOperatorHover(true)} onMouseLeave={() => setCombineOperatorHover(false)} sx={{
      ...inlineDropdownStyle,
      width: '84px',
      lineHeight: '24px'
    }} value={condition.operator} onChange={event => onChange({
      ...condition,
      operator: event.target.value
    })}>
        <MenuItem value={ConditionOperatorEnum.And}>AND</MenuItem>
        <MenuItem value={ConditionOperatorEnum.Or}>OR</MenuItem>
      </Select>
      <ConditionGroup hideBorder={true} sx={{
      padding: 0
    }}>
        {children}
      </ConditionGroup>
      <Tooltip title='Add condition'>
        <IconButton color='primary' onMouseEnter={() => setLocationIndicator({
        index: (condition.conditions?.length ?? 0) - 1,
        location: 'below'
      })} onMouseLeave={() => setLocationIndicator(undefined)} onClick={() => {
        onChange({
          ...condition,
          conditions: [...(condition.conditions ?? []), {
            operator: ''
          }]
        });
        setLocationIndicator({
          index: condition.conditions?.length ?? 0,
          location: 'below'
        });
      }}>
          <i className='material-symbols-add-rounded text-xl' />
        </IconButton>
      </Tooltip>
    </>;
}
function AtomicConditionEditor(t0) {
  const $ = _c(29);
  const {
    condition,
    options,
    supportedProviders,
    supportedResourceTypes,
    onChange
  } = t0;
  const [isAddOpen, setIsAddOpen] = useState(false);
  let t1;
  if ($[0] !== condition.property || $[1] !== options.supportedProperties) {
    let t2;
    if ($[3] !== condition.property) {
      t2 = c => c.name === condition.property;
      $[3] = condition.property;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = options.supportedProperties.flat().find(t2);
    $[0] = condition.property;
    $[1] = options.supportedProperties;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const conditionProperty = t1;
  let t2;
  if ($[5] !== conditionProperty || $[6] !== options.allowUsingProperty) {
    t2 = c_0 => c_0 === conditionProperty || (options.allowUsingProperty ?? _temp)(c_0);
    $[5] = conditionProperty;
    $[6] = options.allowUsingProperty;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const filter = t2;
  let t3;
  if ($[8] !== filter || $[9] !== options.supportedProperties) {
    let t4;
    if ($[11] !== filter) {
      t4 = group => group.filter(filter);
      $[11] = filter;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = options.supportedProperties.map(t4).filter(_temp2);
    $[8] = filter;
    $[9] = options.supportedProperties;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const propertyOptions = t3;
  const Component = conditionProperty?.condition?.Component ?? Noop;
  let t4;
  if ($[13] !== Component || $[14] !== condition || $[15] !== conditionProperty || $[16] !== onChange || $[17] !== propertyOptions || $[18] !== supportedProviders || $[19] !== supportedResourceTypes) {
    t4 = conditionProperty && <Stack direction="row" className="min-h-[32px] h-fit-content" sx={{
      alignItems: "start"
    }}><PropertySelect condition={condition} onChange={onChange} properties={propertyOptions} MenuProps={{
        disablePortal: true
      }} /><OperatorSelect condition={condition} onChange={onChange} propertyRelation={conditionProperty.policyRelation ?? PropertyPolicyRelation.MultipleValues} /><Stack className="max-w-[460px]"><Component condition={condition} onChange={onChange} property={conditionProperty} supportedProviders={supportedProviders} supportedResourceTypes={supportedResourceTypes} /></Stack></Stack>;
    $[13] = Component;
    $[14] = condition;
    $[15] = conditionProperty;
    $[16] = onChange;
    $[17] = propertyOptions;
    $[18] = supportedProviders;
    $[19] = supportedResourceTypes;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== conditionProperty || $[22] !== isAddOpen || $[23] !== onChange || $[24] !== propertyOptions) {
    t5 = !conditionProperty && propertyOptions.length > 0 && <><Tooltip title="Set condition"><IconButton color="primary" onClick={() => setIsAddOpen(true)}><i className="material-symbols-add-rounded text-xl" /></IconButton></Tooltip><PropertySelect open={isAddOpen} properties={propertyOptions} onClose={() => setIsAddOpen(false)} sx={{
        visibility: "hidden",
        height: 0,
        width: 0,
        margin: 0
      }} onChange={onChange} /></>;
    $[21] = conditionProperty;
    $[22] = isAddOpen;
    $[23] = onChange;
    $[24] = propertyOptions;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== t4 || $[27] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
}
function _temp2(g) {
  return g.length > 0;
}
function _temp() {
  return true;
}
function AddFirstCondition(t0) {
  const $ = _c(12);
  const {
    options,
    onChange
  } = t0;
  const [isAddOpen, setIsAddOpen] = useState(false);
  const filter = options.allowUsingProperty ?? _temp3;
  let t1;
  if ($[0] !== filter || $[1] !== options.supportedProperties) {
    let t2;
    if ($[3] !== filter) {
      t2 = group => group.filter(filter);
      $[3] = filter;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = options.supportedProperties.map(t2).filter(_temp4);
    $[0] = filter;
    $[1] = options.supportedProperties;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const propertyOptions = t1;
  if (propertyOptions.length === 0) {
    return null;
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ButtonWithIcon variant="outlined" icon="material-symbols-add-rounded" text="Set Condition" onClick={() => setIsAddOpen(true)} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsAddOpen(false);
    t4 = {
      visibility: "hidden",
      height: 0,
      width: 0,
      margin: 0
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isAddOpen || $[9] !== onChange || $[10] !== propertyOptions) {
    t5 = <>{t2}<PropertySelect open={isAddOpen} properties={propertyOptions} onClose={t3} sx={t4} onChange={onChange} /></>;
    $[8] = isAddOpen;
    $[9] = onChange;
    $[10] = propertyOptions;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp4(g) {
  return g.length > 0;
}
function _temp3() {
  return true;
}
export function ConditionEditor(t0) {
  const $ = _c(26);
  const {
    condition,
    options,
    onChange,
    supportedProviders,
    supportedResourceTypes
  } = t0;
  const t1 = condition?.operator === ConditionOperatorEnum.And ? ConditionOperatorEnum.Or : ConditionOperatorEnum.And;
  let t2;
  if ($[0] !== condition) {
    t2 = [condition].filter(Boolean);
    $[0] = condition;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      operator: t1,
      conditions: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const virtualWrapper = t3 as Condition;
  let t4;
  if ($[5] !== onChange) {
    t4 = condition_0 => {
      if (condition_0.conditions?.length === 0) {
        onChange(undefined);
      } else {
        if (condition_0.conditions?.length === 1) {
          onChange(condition_0.conditions?.[0]);
        } else {
          onChange({
            operator: condition_0.operator ?? ConditionOperatorEnum.And,
            conditions: condition_0.conditions
          });
        }
      }
    };
    $[5] = onChange;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onConditionChange = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      display: "inline-flex"
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = `${virtualWrapper.operator} root`;
  let t7;
  if ($[8] !== onConditionChange || $[9] !== options || $[10] !== supportedProviders || $[11] !== supportedResourceTypes || $[12] !== virtualWrapper) {
    t7 = <ConditionGroupEditor supportedProviders={supportedProviders} supportedResourceTypes={supportedResourceTypes} condition={virtualWrapper} options={options} onChange={onConditionChange} />;
    $[8] = onConditionChange;
    $[9] = options;
    $[10] = supportedProviders;
    $[11] = supportedResourceTypes;
    $[12] = virtualWrapper;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== condition || $[15] !== onChange || $[16] !== onConditionChange || $[17] !== options || $[18] !== supportedProviders || $[19] !== supportedResourceTypes || $[20] !== virtualWrapper) {
    t8 = condition ? <AtomicConditionEditor condition={{
      operator: ""
    }} options={options} supportedProviders={supportedProviders} supportedResourceTypes={supportedResourceTypes} onChange={newCondition => onConditionChange({
      ...virtualWrapper,
      conditions: [...virtualWrapper.conditions, newCondition]
    })} /> : <AddFirstCondition options={options} onChange={onChange} />;
    $[14] = condition;
    $[15] = onChange;
    $[16] = onConditionChange;
    $[17] = options;
    $[18] = supportedProviders;
    $[19] = supportedResourceTypes;
    $[20] = virtualWrapper;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = <ConditionContainer sx={t5} className={t6}>{t7}{t8}</ConditionContainer>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}